// src/pages/AdminAccessCheck.tsx
import { AdminPreloader } from '@/components/admin/AdminPreloader';
import { useSession } from '@/hooks/useSession';
import { AdminSecurity } from '@/lib/rpc/admin-api';
import { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

type GateState =
  | { kind: 'boot' }
  | { kind: 'checking' }
  | { kind: 'allowed' }
  | { kind: 'denied' }
  | { kind: 'error'; message: string };

export default function AdminAccessCheck() {
  const { status } = useSession();
  const navigate = useNavigate();
  const [state, setState] = useState<GateState>({ kind: 'boot' });
  const lastStatusRef = useRef<typeof status | null>(null);
  const checkingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    if (status === 'loading' || status === 'recovering') {
      lastStatusRef.current = status;
      if (status === 'recovering') {
        setState(prev => (prev.kind === 'allowed' ? prev : { kind: 'checking' }));
      }
      return () => {
        cancelled = true;
      };
    }

    if (status === 'anon') {
      lastStatusRef.current = 'anon';
      setState({ kind: 'denied' });
      navigate('/auth', { replace: true });
      return () => {
        cancelled = true;
      };
    }

    if (status === 'authed') {
      if (checkingRef.current || lastStatusRef.current === 'authed') {
        lastStatusRef.current = 'authed';
        return () => {
          cancelled = true;
        };
      }

      checkingRef.current = true;
      lastStatusRef.current = 'authed';

      const runAdminCheck = async () => {
        setState({ kind: 'checking' });
        const res = await AdminSecurity.isAdminSimple();
        if (cancelled) return;

        if (res.error) {
          if (import.meta.env.DEV) console.debug('[AdminAccessCheck] gate error', res.error);
          setState({ kind: 'error', message: 'Admin gate check failed' });
          return;
        }
        if (res.data === true) {
          setState({ kind: 'allowed' });
        } else {
          setState({ kind: 'denied' });
          navigate('/', { replace: true });
        }
      };

      runAdminCheck().finally(() => {
        checkingRef.current = false;
      });

      return () => {
        cancelled = true;
      };
    }

    return () => {
      cancelled = true;
    };
  }, [status, navigate]);

  if (state.kind === 'allowed') return <Outlet />;

  // Determine message based on state
  const getMessage = () => {
    switch (state.kind) {
      case 'checking':
      case 'boot':
        return 'Checking admin access...';
      case 'denied':
        return 'Redirecting...';
      case 'error':
        return state.message || 'Admin check failed';
      default:
        return 'Loading...';
    }
  };

  return <AdminPreloader variant="checking" message={getMessage()} />;
}
